import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { View, Text, ScrollView } from "react-native";

import { isWeb, navigate } from "../../../helpers/utils";
import EmptyPageIcons from "../common/icons/empty-pages";
import ManaButton from "../common/ManaButton";
import CountDownTimer from "./CountDownTimer";
import appStyles from "../../appStyles";
import TrackHomeCalendar from "../journal/calendar/TrackHomeCalendar";
import EmptyTrackFabButton from "./EmptyTrackFabButton";

import styles from "./emptyPageStyles";
import IntegrationMappingEmpty from "../integrations/mapping/IntegrationMappingEmpty";
import fontStyles from "../../fonts";
import AppTheme from "../AppTheme";
import appPages from "../../../appPages";
import GenericOverlay from "./GenericOverlay";

const EmptyTrackPage = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    showMariMessage: (message) => setMessage(message),
  }));

  const {
    controller,
    userCreatedTime,
    journalContext,
    selectedDate,
    hasEnabledIntegrations,
    openOverlay = false,
    enableManualMode = () => {},
    closeTutorialOverlay = () => {},
  } = props;
  const [showOverlay, setShowOverlay] = useState(false);
  const [message, setMessage] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  useEffect(() => {
    setShowOverlay(!!openOverlay);
  }, [openOverlay]);

  const closeOverlay = () => {
    setShowOverlay(false);
    setMessage("");

    // User is for the 1st time on the page and closes the tutorial
    if (openOverlay) {
      return closeTutorialOverlay();
    }

    // CLosing after manual mode was pressed or timer finished
    if (manualMode || timerExpired) {
      enableManualMode({
        isAutoTrackMode: !manualMode,
        showEmptyPage: timerExpired ? false : true,
      });
    }
  };

  const trackTimeManually = () => {
    setShowOverlay(true);
    setManualMode(true);
  };

  const goToMappingGamePage = () => {
    navigate(controller, appPages.integrationsMappingGame);
  };

  const onTimerExpired = () => {
    setTimerExpired(true);
    setShowOverlay(true);
  };

  return (
    <View style={[appStyles.container]}>
      {/* Header */}
      <TrackHomeCalendar
        isFromEmptyPage
        journalContext={journalContext}
        selectedDate={selectedDate}
      />

      {showOverlay ? (
        <GenericOverlay
          ImageTag={EmptyPageIcons.TrackOverlay}
          onButtonPress={closeOverlay}
        />
      ) : null}

      {!hasEnabledIntegrations ? (
        /* Empty integrations state */
        <ScrollView
          style={[appStyles.scrollView, { marginTop: 16 }]}
          contentContainerStyle={appStyles.scrollViewContent}
        >
          <IntegrationMappingEmpty controller={controller} />
        </ScrollView>
      ) : manualMode ? null : (
        /* Content */
        <View style={[styles.container]}>
          <View style={styles.imageContainer}>
            <EmptyPageIcons.Track />
          </View>

          <CountDownTimer
            title="Your first day of autotracked time will be ready in"
            time={userCreatedTime}
            onDone={onTimerExpired}
          />

          <ManaButton
            isMainButton
            text="Help us understand your time"
            style={styles.button}
            styleText={styles.buttonText}
            onPress={goToMappingGamePage}
          />
          <ManaButton
            text="Track Time Manually"
            style={styles.button}
            styleText={styles.buttonText}
            onPress={trackTimeManually}
          />
        </View>
      )}

      {!showOverlay ? (
        <EmptyTrackFabButton
          style={[
            appStyles.fab,
            !hasEnabledIntegrations ? { bottom: 120 } : {},
          ]}
          aboutThisScreen={() => setShowOverlay(true)}
          mariOptions={
            message
              ? {
                  mariMessage: {
                    message,
                    isFromEmptyState: !!message,
                  },
                }
              : null
          }
        />
      ) : null}

      {!hasEnabledIntegrations ? (
        <View style={[appStyles.footerButtons, { bottom: isWeb ? 50 : 60 }]}>
          <Text
            style={[fontStyles.h5, { color: AppTheme.DEFAULT_COLOR }]}
            onPress={trackTimeManually}
          >
            Or Track Time Manually
          </Text>
        </View>
      ) : null}
    </View>
  );
});

export default EmptyTrackPage;
